import { filterPredicateIsTruthy } from 'utility/Check/filterPredicateIsTruthy';

import {
    AccommodationFilter,
    CancellationType,
    Filter,
    FlightFilter,
    PaymentTypeFilter,
    RangeValue,
    SpecialFilter,
    TourOperatorCategory,
    TransferFilter,
} from 'types/Search/filter';
import {
    paramTransferValue,
    paramTruthyValue,
    SearchParam,
} from 'types/Search/params';

import { ViewType } from 'proto/Offer/Accommodation/ViewType';

import { castToType, castToTypeArray } from './castToType';
import { convertUrlValueToAccommodationCategory } from './Filter/accommodationCategory';
import { convertParamsToAttributesList } from './Filter/attributeList';
import { convertUrlValueToExterior } from './Filter/exterior';
import { convertUrlValueToMealType } from './Filter/mealType';
import { convertUrlValueToPoolType } from './Filter/poolType';
import { convertUrlValueToRoomClass } from './Filter/roomClass';
import { convertUrlValueToRoomType } from './Filter/roomType';
import { convertUrlValueToViewType } from './Filter/viewType';

const parseBoolean = (value: string | null) => value === paramTruthyValue;

const convertToRangeValue = (
    min: string | null,
    max: string | null
): RangeValue<number> | undefined => {
    if (min || max) {
        return {
            min: min ? parseInt(min) : null,
            max: max ? parseInt(max) : null,
        };
    }
};

const convertUrlValueToType = <T>(
    urlParams: URLSearchParams,
    param: SearchParam,
    converterFunc: (value: string) => T | undefined
): T[] => {
    const searchParam = urlParams.get(param);
    if (searchParam) {
        return searchParam
            .split(',')
            .map(converterFunc)
            .filter(filterPredicateIsTruthy);
    }

    return [];
};

const convertToBudget = (
    urlParams: URLSearchParams
): RangeValue<number> | undefined => {
    const budgetMinParam = urlParams.get(SearchParam.BudgetMin);
    const budgetMaxParam = urlParams.get(SearchParam.BudgetMax);

    return convertToRangeValue(budgetMinParam, budgetMaxParam);
};

const convertToTourOperatorList = (urlParams: URLSearchParams): Set<string> => {
    const tourOperatorListParam = urlParams.get(SearchParam.TouroperatorList);
    if (tourOperatorListParam) {
        return new Set(tourOperatorListParam.split(','));
    }

    return new Set();
};

const convertToFlight = (urlParams: URLSearchParams): FlightFilter => {
    const airlineListParam = urlParams.get(SearchParam.AirlineList);
    const destinationAirportListParam = urlParams.get(
        SearchParam.DestinationAirportList
    );
    const directFlightParam = urlParams.get(SearchParam.DirectFlight);
    const hideMixFlightsParam = urlParams.get(SearchParam.HideMixFlights);
    const withNoBagParam = urlParams.get(SearchParam.WithNoBag);
    const departureFlightTimeFromParam = urlParams.get(
        SearchParam.DepartureFlightTimeFrom
    );
    const departureFlightTimeUntilParam = urlParams.get(
        SearchParam.DepartureFlightTimeUntil
    );
    const returnFlightTimeFromParam = urlParams.get(
        SearchParam.ReturnFlightTimeFrom
    );
    const returnFlightTimeUntilParam = urlParams.get(
        SearchParam.ReturnFlightTimeUntil
    );

    return {
        airlineList: airlineListParam
            ? new Set(airlineListParam.split(','))
            : new Set(),
        destinationAirportList: destinationAirportListParam
            ? new Set(destinationAirportListParam.split(','))
            : new Set(),
        directFlight: parseBoolean(directFlightParam),
        hideMixFlights: parseBoolean(hideMixFlightsParam),
        withNoBag: parseBoolean(withNoBagParam),
        departureFlightTime: convertToRangeValue(
            departureFlightTimeFromParam,
            departureFlightTimeUntilParam
        ),
        returnFlightTime: convertToRangeValue(
            returnFlightTimeFromParam,
            returnFlightTimeUntilParam
        ),
    };
};

const convertToTransfer = (urlParams: URLSearchParams): TransferFilter => {
    const hotelTransferParam = urlParams.get(SearchParam.HotelTransfer);
    const privateTransferParam = urlParams.get(SearchParam.PrivateTransfer);
    const railAndFlyParam = urlParams.get(SearchParam.RailAndFly);

    return {
        hotelTransfer: hotelTransferParam === paramTransferValue,
        privateTransfer: parseBoolean(privateTransferParam),
        railAndFly: parseBoolean(railAndFlyParam),
    };
};

const convertToPaymentTypeList = (
    urlParams: URLSearchParams
): Set<PaymentTypeFilter> => {
    const paymentTypeListParam = urlParams.get(SearchParam.PaymentTypeList);
    if (paymentTypeListParam) {
        return new Set(castToTypeArray(paymentTypeListParam.split(',')));
    }

    return new Set();
};

const convertToTourOperatorCategoryList = (
    urlParams: URLSearchParams
): Set<TourOperatorCategory> => {
    const tourOperatorCategoryListParam = urlParams.get(
        SearchParam.TourOperatorCategoryList
    );
    if (tourOperatorCategoryListParam) {
        return new Set(
            castToTypeArray(tourOperatorCategoryListParam.split(','))
        );
    }

    return new Set();
};

const getCancellationType = (urlParams: URLSearchParams) => {
    const isCancellationCost =
        urlParams.get(SearchParam.CancellationCost) === paramTruthyValue;
    const isCancellationFree =
        urlParams.get(SearchParam.CancellationFree) === paramTruthyValue;
    const isCancellation24Hours =
        urlParams.get(SearchParam.Cancellation24Hours) === paramTruthyValue;

    if (isCancellationCost) {
        return CancellationType.Cost;
    }
    if (isCancellationFree) {
        return CancellationType.Free;
    }
    if (isCancellation24Hours) {
        return CancellationType.Free24Hour;
    }
};

const convertToSpecial = (urlParams: URLSearchParams): SpecialFilter => {
    const lowDepositParam = urlParams.get(SearchParam.LowDeposit);
    const rebookableParam = urlParams.get(SearchParam.Rebookable);
    const reservableParam =
        urlParams.get(SearchParam.ReservablePredicted) ||
        urlParams.get(SearchParam.Reservable);
    const promoDiscountParam = urlParams.get(SearchParam.PromoDiscount);

    return {
        cancellation: getCancellationType(urlParams),
        lowDeposit: parseBoolean(lowDepositParam),
        rebookable: parseBoolean(rebookableParam),
        reservable: parseBoolean(reservableParam),
        promoDiscount: parseBoolean(promoDiscountParam),
    };
};

const convertToViewType = (params: URLSearchParams) => {
    const viewTypeList: ViewType[] = [];
    if (params.has(SearchParam.ViewTypeList)) {
        viewTypeList.push(
            ...convertUrlValueToType(
                params,
                SearchParam.ViewTypeList,
                convertUrlValueToViewType
            )
        );
    }
    if (params.get(SearchParam.OceanView) === paramTruthyValue) {
        viewTypeList.push(ViewType.SeaView);
    }
    if (params.get(SearchParam.PartialOceanView) === paramTruthyValue) {
        viewTypeList.push(ViewType.LateralSeaView);
    }
    return new Set(viewTypeList);
};

const convertToAccommodation = (
    urlParams: URLSearchParams
): AccommodationFilter => {
    const hotelCategoryListParam = urlParams.get(SearchParam.HotelCategoryList);
    const ratingParam = urlParams.get(SearchParam.Rating);
    const withHygieneFactsParam = urlParams.get(SearchParam.WithHygieneFacts);

    return {
        mealTypeList: new Set(
            convertUrlValueToType(
                urlParams,
                SearchParam.MealTypeList,
                convertUrlValueToMealType
            )
        ),
        roomTypeList: new Set(
            convertUrlValueToType(
                urlParams,
                SearchParam.RoomTypeList,
                convertUrlValueToRoomType
            )
        ),
        roomClassList: new Set(
            convertUrlValueToType(
                urlParams,
                SearchParam.RoomClassList,
                convertUrlValueToRoomClass
            )
        ),
        viewTypeList: convertToViewType(urlParams),
        poolTypeList: new Set(
            convertUrlValueToType(
                urlParams,
                SearchParam.PoolTypeList,
                convertUrlValueToPoolType
            )
        ),
        exteriorList: new Set(
            convertUrlValueToType(
                urlParams,
                SearchParam.ExteriorList,
                convertUrlValueToExterior
            )
        ),
        hotelCategoryList: new Set(
            castToTypeArray(hotelCategoryListParam?.split(','))
        ),
        rating: ratingParam ? castToType(ratingParam) : undefined,
        accommodationCategoryList: new Set(
            convertUrlValueToType(
                urlParams,
                SearchParam.AccommodationCategoryList,
                convertUrlValueToAccommodationCategory
            )
        ),
        withHygieneFacts: parseBoolean(withHygieneFactsParam),
        attributesList: convertParamsToAttributesList(urlParams),
    };
};

const convertKIFilterParams = (searchParams: URLSearchParams): string[] => {
    const kiFilterParam = searchParams.get(SearchParam.KiFilter);
    if (!kiFilterParam) {
        return [];
    }
    return kiFilterParam
        .split(',')
        .map((label) => label.trim())
        .filter(Boolean);
};

export const convertUrlToFilter = (searchUrl: string): Filter => {
    const { searchParams } = new URL(searchUrl);
    // convert searchParams into Filter
    return {
        budget: convertToBudget(searchParams),
        tourOperatorList: convertToTourOperatorList(searchParams),
        flight: convertToFlight(searchParams),
        transfer: convertToTransfer(searchParams),
        paymentTypeList: convertToPaymentTypeList(searchParams),
        tourOperatorCategoryList:
            convertToTourOperatorCategoryList(searchParams),
        special: convertToSpecial(searchParams),
        accommodation: convertToAccommodation(searchParams),
        kiFilter: convertKIFilterParams(searchParams),
    };
};
